import React, { useState } from "react";
import { Box, Button, InputBase, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { IconSearch } from "@tabler/icons-react";

import { ragSearchResume } from "../../../services/resumeService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { getInitials } from "../Console/candidateRow";
import tokens from "../../theme/tokens";

const { color, font, radius, shadow, avatarPalette } = tokens;

const SUGGESTIONS = [
  "React engineers in Bengaluru",
  "5+ years backend Python",
  "Product designers with fintech experience",
];

const tintFor = (id) => {
  const s = String(id ?? "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
};

/**
 * Semantic search across every resume ever received (RAG endpoint).
 *
 * The design mocks show a "96% match" chip and a skills/domain/location blurb per
 * result. The API returns only Candidate Name, Reason and Phone, so this renders the
 * model's `Reason` as the blurb rather than inventing a score. See
 * docs/backend-spec-console.md if we want the match score surfaced.
 */
const ResumePoolSearch = () => {
  const dispatch = useDispatch();

  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const search = async (term = keyword) => {
    if (!term.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      // company_id is derived from the JWT server-side; passing it does nothing.
      const res = await ragSearchResume({ query: term });
      if (res.error) throw new Error(res.message || "Search failed.");

      // Sort by match strength so the bar below is meaningful: it's a ranking signal,
      // not an absolute score.
      const candidates = [...(res.Candidates || [])].sort(
        (a, b) => (b["Match Score"] ?? 0) - (a["Match Score"] ?? 0)
      );
      setResults(candidates);
      setCount(res["Candidates Fetched"] || candidates.length);
    } catch (e) {
      setResults([]);
      setCount(0);
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: e.message || "Failed to search resumes.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // "Match Score" is a RAW COSINE SIMILARITY in [0,1] — not a percentage, and not a
  // probability. Real matches cluster around 0.3–0.7, so rendering "62% match" would make
  // a strong hit look like a coin flip, and rescaling it until it reads "96%" would just be
  // inventing a number. What the score IS good for is ordering, so we show each result's
  // strength RELATIVE to the best hit in this result set, and no number at all.
  const topScore = results[0]?.["Match Score"] ?? 0;
  const relativeStrength = (score) => {
    if (!topScore || score == null) return 0;
    return Math.max(0.12, Math.min(1, score / topScore));
  };

  return (
    <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 5 }}>
      <Box sx={{ maxWidth: 720, mx: "auto" }}>
        <Box sx={{ font: `600 18px ${font.sans}`, color: color.text, mb: 2.5 }}>
          Search every resume you've ever received
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            bgcolor: color.surface,
            border: `1px solid ${color.borderInput}`,
            borderRadius: `${radius.card}px`,
            boxShadow: shadow.card,
            px: 1.75,
            py: 1.5,
          }}
        >
          <IconSearch size={17} stroke={1.75} color={color.textMuted} />
          <InputBase
            autoFocus
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Describe who you're looking for…"
            sx={{
              flex: 1,
              font: `400 13.5px ${font.sans}`,
              "& input::placeholder": { color: color.textMuted, opacity: 1 },
            }}
          />
          <Button variant="contained" onClick={() => search()} disabled={loading || !keyword.trim()}>
            Search
          </Button>
        </Box>

        <Box sx={{ mt: 1.75, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ font: `400 11px ${font.mono}`, color: color.textMuted }}>try:</Box>
          {SUGGESTIONS.map((s) => (
            <Box
              key={s}
              onClick={() => {
                setKeyword(s);
                search(s);
              }}
              sx={{
                cursor: "pointer",
                border: `1px solid ${color.borderInput}`,
                borderRadius: `${radius.pill}px`,
                px: 1.25,
                py: 0.5,
                font: `400 11px ${font.sans}`,
                color: color.textSecondary,
                "&:hover": { bgcolor: color.rowTint, borderColor: color.indigo },
              }}
            >
              {s}
            </Box>
          ))}
        </Box>

        {loading && (
          <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && searched && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ font: `400 11.5px ${font.mono}`, color: color.textMuted, mb: 1.5 }}>
              {count} CANDIDATE{count === 1 ? "" : "S"} MATCHED
            </Box>

            {!results.length && (
              <Box sx={{ font: `400 13px ${font.sans}`, color: color.textSecondary }}>
                No resumes matched that search.
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {results.map((c, i) => {
                const id = c["Resume ID"] || i;
                const tint = tintFor(id);
                const name = c["Candidate Name"] || "Unknown";
                return (
                  <Box
                    key={id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                      bgcolor: color.surface,
                      border: `1px solid ${color.border}`,
                      borderRadius: `${radius.card}px`,
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        flex: "none",
                        display: "grid",
                        placeItems: "center",
                        borderRadius: `${radius.control}px`,
                        bgcolor: tint.bg,
                        color: tint.fg,
                        font: `600 11px ${font.sans}`,
                      }}
                    >
                      {getInitials(name)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ font: `500 13.5px ${font.sans}`, color: color.text }}>
                          {name}
                        </Box>
                        {c["Source Job"] && (
                          <Box
                            component="span"
                            sx={{
                              font: `400 10.5px ${font.sans}`,
                              color: color.textMuted,
                              border: `1px solid ${color.border}`,
                              borderRadius: `${radius.chip}px`,
                              px: 0.75,
                              py: 0.125,
                            }}
                          >
                            {c["Source Job"]}
                          </Box>
                        )}
                      </Box>

                      {c.Reason && (
                        <Box
                          sx={{
                            mt: 0.5,
                            font: `400 11.5px/1.6 ${font.sans}`,
                            color: color.textMuted,
                          }}
                        >
                          {c.Reason}
                        </Box>
                      )}

                      <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1.25 }}>
                        {/* Strength relative to the best hit in this result set. No number:
                            the underlying value is a cosine similarity, which ranks well
                            but means nothing as a percentage. */}
                        <Box
                          sx={{
                            width: 68,
                            height: 4,
                            flex: "none",
                            borderRadius: 2,
                            bgcolor: color.borderRow,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: 4,
                              width: `${relativeStrength(c["Match Score"]) * 100}%`,
                              bgcolor: color.indigo,
                            }}
                          />
                        </Box>
                        {Array.isArray(c["Matched Fields"]) && c["Matched Fields"].length > 0 && (
                          <Box sx={{ font: `400 10.5px ${font.mono}`, color: color.textMuted }}>
                            matched on {c["Matched Fields"].join(", ").replace(/_/g, " ")}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {c.Phone && (
                      <Box
                        sx={{
                          flex: "none",
                          font: `400 11px ${font.mono}`,
                          color: color.indigo,
                          bgcolor: color.indigoTint,
                          borderRadius: `${radius.chip}px`,
                          px: 0.875,
                          py: 0.375,
                        }}
                      >
                        {c.Phone}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ResumePoolSearch;
